import cls from "./products.module.scss";
import Link from "next/link";
import React, {FC} from "react";
import {CheckToastSvg, FailSvg, HeartSvg} from "../../../svg";
import {useToast} from "../../../atoms/Toast/useToast";
import {domainUrl} from "../../../helper";
import {useDispatch, useSelector} from "react-redux";
import {IGlobalProduct, IState} from "../../../types/state";
import {IBlockProductProps} from "./BlockProducts";
import {useRouter} from "next/router";
import {IProduct} from "../../../types/homePageTypes";
import {wishlistAddItem, wishlistRemoveItem} from "../../../store/wishlist";
import {handleAddCard, openUpCrossProd} from "../../shared/ProductCard/helpers";
import {ICurrencies} from "../Wrappers/MainLayout";
import {handleAmdPrice, handlePriceCommasFormat} from "../../../controllers/helpers";

interface IProductListProps extends Omit<IBlockProductProps, "products"> {
    product: IProduct;
    priority?: boolean;
}

const ProductCard: FC<IProductListProps> = (
    {
        product,
        dbName,
        translationsData,
        selectedCurrItems: {
            selectedCurrCode,
            selectedCurrRate,
            selectedCurrSymbol
        },
        selectedLocale,
        showWishlist,
        showCart,
        priority,
        loading = "lazy"
    }
) => {
    const toast = useToast();
    const dispatch = useDispatch();
    const {push: routerPush} = useRouter();
    const secondImage = product.images[1];
    const cartToken = useSelector((state: IState) => state.cartToken);
    const customer = useSelector((state: IState) => state.customer);
    const wishlist = useSelector((state: IState) => state.wishlist)
    const isProductInWishList = wishlist?.find((item: IGlobalProduct) => item.id === product.id)
    const signed = useSelector((state: IState) => state.customer.authenticated);
    const {
        id: productId,
        flats: [{
            url_key = "",
            name: productName = "",
            short_description: productShortDesc = "",
            price: productPrice = "",
            special_price: productSpecialPrice = ""
        } = {}] = [],
        images: [imagePath] = [],
        isProductInStock
    } = product;
    const addToast = (error?: boolean) => {
        if (error) {
            toast.open(
                <span className="d-flex chek-fms">
                    <FailSvg/>
                    {translationsData.ProductNotAvailable}
                </span>
            )
        } else {
            if (isProductInStock) {
                toast.open(
                    <span className="d-flex chek-fms">
                        <CheckToastSvg/>
                        {translationsData.ToastAddToCart}
                   </span>
                )
            }
        }
    }
    const cartDataObj = {
        product,
        quantity: 1,
        cartToken,
        customer: customer as { token: string },
        selectedLocale,
        selectedCurrCode,
        optionsObject: null,
        addToast
    }

    const handleWishIconClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault();
        if (!signed) {
            toast.open(
                <span className="d-flex faild-toast-fms">
                    <FailSvg/>
                    {translationsData.ToastSignIn}
                </span>
            )
        } else {
            !!isProductInWishList ?
                dispatch(wishlistRemoveItem(product.id))
                : dispatch(wishlistAddItem(product, selectedLocale))
            const toastChildren = {
                "true": {svg: <FailSvg/>, title: translationsData?.ToastWishListRemove},
                "false": {svg: <CheckToastSvg/>, title: translationsData?.ToastWishList}
            }
            toast.open(
                <span className="d-flex faild-toast-fms">
                    {toastChildren[`${!!isProductInWishList}`].svg}
                    {toastChildren[`${!!isProductInWishList}`].title}
                </span>
            )
        }
    }
    const handleAddCartClickLocal = async () => {
        return await handleAddCard({
            openUpCrossProd: () => {
                return openUpCrossProd(
                    {
                        dispatch,
                        productId,
                        selectedLocale,
                        selectedRate: {
                            exchange_rate: {rate: selectedCurrRate},
                            symbol: selectedCurrSymbol,
                            code: selectedCurrCode
                        } as ICurrencies,
                        product
                    }
                )
            },
            dispatch,
            data: cartDataObj,
            routerPush,
            isProductInStock,
        })
    }
    const positionSymbolHandle = (whichPrice?: string | number) => {
        if (selectedCurrCode === "AMD") {
            return `${whichPrice ?? productPrice} ${selectedCurrSymbol}`;
        }

        return `${selectedCurrSymbol} ${whichPrice ?? productPrice}`;
    };

    return (
        <div className={cls["product-card"]}>
            <div className={`${cls["product-card__image"]}`}>
                <Link href={`/${url_key}`} prefetch={priority}>
                    <div className={`${cls["product-image__body"]} ${cls["product-image__body-fms"]}`}>
                        {!secondImage && <div className={`${cls["item_overlay"]} ${cls["hide-for-tablet"]}`}/>}
                        <div className={cls["img_btn_wrapper"]}>
                            {showCart ?
                                <button
                                    type="button"
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        await handleAddCartClickLocal()
                                    }}
                                    className={`btn btn-primary ${cls[`product-card__addToCart`]} ${cls["hide-for-tablet"]}`}
                                >
                                    {translationsData.AddToCartButtonTitle}
                                </button>
                                : null
                            }
                            {showWishlist ? <div className={cls["product-card__wish_icon"]}>
                                <div
                                    onClick={handleWishIconClick}
                                    className={`btn-light btn-svg-icon btn-svg-icon--fake-svg ${cls["product-card__wishlist"]}`}
                                >
                                    <div className={cls["wishlist-icon-wrapper"]}>
                                        <HeartSvg className={isProductInWishList ? cls["inner-wishlist"] : ""}/>
                                    </div>
                                </div>
                            </div> : null}
                        </div>
                        <div className={cls["img_wrapper"]}>
                            <img
                                src={domainUrl(`${dbName}${imagePath}`)}
                                alt="product card image"
                                loading={loading as "eager" | "lazy"}
                                width="280"
                                height="280"
                                className={`${cls.first_image} ${secondImage && cls.hide_first_image}`}
                            />
                            {secondImage && <img
                                src={domainUrl(`${dbName}${secondImage}`)}
                                alt="product card image"
                                loading="lazy"
                                width="280"
                                height="280"
                                className={cls.second_image}
                            />}
                        </div>
                    </div>
                </Link>
            </div>
            <div className={cls["product-card__info"]}>
                <div className={cls["product-card__name"]}>
                    <Link href={`/${url_key}`} prefetch={priority}>
                        {productName}
                    </Link>
                </div>
                {/*<div className={cls["product-card-description"]}>*/}
                {/*    <span dangerouslySetInnerHTML={{__html: productShortDesc}}/>*/}
                {/*</div>*/}
            </div>
            <div className={cls["product-card__actions"]}>
                <div className={cls["product-card__availability-mobile"]}>
                    <div className={cls["product-card__availability"]}>
                        {translationsData.ProductAvailable}
                        <span className="text-success">
                            {translationsData.ProductInStock}
                        </span>
                    </div>
                    {productSpecialPrice ? (
                        <div className={cls.prices}>
                            <div className={cls.new_price}>
                                {positionSymbolHandle(productSpecialPrice)}
                            </div>
                            <div className={cls.old_price}>
                                {positionSymbolHandle()}
                            </div>
                        </div>
                    ) : <div className={cls.prices}>
                        {positionSymbolHandle()}
                    </div>}
                </div>
                {showCart ? <button
                    type="button"
                    onClick={async (e) => {
                        e.preventDefault();
                        await handleAddCartClickLocal()
                    }}
                    className={`btn btn-primary ${cls[`product-card__addToCart`]} ${cls["hide-for-desktop"]}`}
                >
                    {translationsData.AddToCartButtonTitle}
                </button> : null}
            </div>
        </div>
    )
}

export default ProductCard;
